import React from 'react';

import { FacebookPost } from './FacebookPost';
import { InstagramPost } from './InstagramPost';
import { InstagramReel } from './InstagramReel';
import { InstagramStory } from './InstagramStory';
import { LinkedInPost } from './LinkedInPost';
import { PinterestPin } from './PinterestPin';
import { ThreadsPost } from './ThreadsPost';
import { type TikTokPreviewProps, TikTokVideo } from './TikTokVideo';
import { XPost } from './XPost';
import { YouTubeShort } from './YouTubeShort';
import {
  type PlatformPreviewBaseProps,
  type PlatformPreviewKind,
  type PlatformPreviewSizeMode,
  platformNaturalMaxWidth,
} from './types';

import { cn } from '@/lib/utils';

export interface PlatformPreviewProps extends PlatformPreviewBaseProps, TikTokPreviewProps {
  kind: PlatformPreviewKind;
  sizeMode?: PlatformPreviewSizeMode;
  /** LinkedIn-only headline shown under the brand name. */
  headline?: string;
  /** TikTok / YouTube Short — sound credit shown bottom-left. */
  soundCredit?: string;
  /** Pinterest — board name shown next to the brand name. */
  boardName?: string;
  /** LinkedIn-only article card under the post (headline, domain, image), as the publisher will attach it. */
  article?: { title?: string; domain: string; imageUrl?: string | null } | null;
}

/**
 * Unified presentation dispatcher — renders Mora's approximation of the
 * requested platform surface. Use this for editor/showcase composition only;
 * provider capability and publish-payload truth belong to the adapters and
 * platform contract tests. Use the per-platform shell directly only when the
 * kind is statically known.
 */
export const PlatformPreview: React.FC<PlatformPreviewProps> = ({
  kind,
  sizeMode = 'natural',
  headline,
  soundCredit,
  tiktokMediaKind,
  tiktokSound,
  tiktokCoverLabel,
  boardName,
  article,
  ...rest
}) => {
  let content: React.ReactNode = null;

  switch (kind) {
    case 'instagram-post':
      content = <InstagramPost {...rest} />;
      break;
    case 'instagram-story':
      // Task A5: dedicated Story presentation shell — segmented progress bars, top
      // identity header + close, bottom reply bar. Was InstagramPost until
      // this task shipped (Story wasn't reachable/publishable before A5).
      content = <InstagramStory {...rest} />;
      break;
    case 'instagram-reel':
      content = <InstagramReel {...rest} soundCredit={soundCredit} />;
      break;
    case 'tiktok':
      content = (
        <TikTokVideo
          {...rest}
          soundCredit={soundCredit}
          tiktokMediaKind={tiktokMediaKind}
          tiktokSound={tiktokSound}
          tiktokCoverLabel={tiktokCoverLabel}
        />
      );
      break;
    case 'tiktok-photo':
      // Reuses the video shell until a dedicated photo-carousel presentation
      // lands — the safe-zone chrome difference is handled by the caller
      // (PostBody reads the resolved row's hasChrome, not this component),
      // not by this shell itself.
      content = (
        <TikTokVideo
          {...rest}
          soundCredit={soundCredit}
          tiktokMediaKind={tiktokMediaKind}
          tiktokSound={tiktokSound}
          tiktokCoverLabel={tiktokCoverLabel}
        />
      );
      break;
    case 'facebook-post':
    case 'facebook-reel':
    case 'facebook-story':
      // Reel + Story reuse the Facebook feed shell until dedicated presentation
      // shells land (Phase 4 of the accurate-preview milestone).
      content = <FacebookPost {...rest} />;
      break;
    case 'linkedin-post':
      content = <LinkedInPost {...rest} headline={headline} article={article} />;
      break;
    case 'x-post':
      content = <XPost {...rest} />;
      break;
    case 'youtube-short':
    case 'youtube-landscape':
      // Landscape reuses the Short presentation shell until a dedicated one lands.
      content = <YouTubeShort {...rest} soundCredit={soundCredit} />;
      break;
    case 'pinterest-pin':
      content = <PinterestPin {...rest} boardName={boardName} />;
      break;
    case 'threads-post':
      content = <ThreadsPost {...rest} />;
      break;
    default: {
      const _exhaustive: never = kind;
      void _exhaustive;
      content = null;
      break;
    }
  }

  if (!content) return null;
  if (sizeMode === 'fill') return <>{content}</>;

  return (
    <div
      className={cn('mx-auto w-full')}
      style={{ maxWidth: `${platformNaturalMaxWidth(kind)}px` }}
    >
      {content}
    </div>
  );
};
